import { useState, useCallback } from 'react';
import { route } from '../utils/route';
import { getCSRFToken } from '../utils/csrf';
import { trackPackAdded } from '../utils/tracking';
import type { ProductPack, ProductPackItem } from '../types/packs';

interface UseAddPackProps {
  onSuccess: (pack: ProductPack, itemKeys?: string[]) => void;
  onRemoveSuccess: (packId: number) => void;
  refreshCart: () => Promise<void>;
  source?: string;
}

async function postItems(items: { id: number; format_id: number; quantity: number; source?: string }[]): Promise<void> {
  const response = await fetch(route('api.cart.add_item'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': getCSRFToken(),
    },
    body: JSON.stringify({ items }),
  });
  if (!response.ok) throw new Error('Failed to update cart');
}

function itemsToCartPayload(items: ProductPackItem[], source: string = 'pack') {
  return items.map(item => ({ id: item.product_id, format_id: item.product_format_id, quantity: item.quantity, source }));
}

function itemsToKeys(items: ProductPackItem[]) {
  return items.map(i => `${i.product_id}:${i.product_format_id}`);
}

export function useAddPack({ onSuccess, onRemoveSuccess, refreshCart, source = 'pack' }: UseAddPackProps) {
  const [loadingPackId, setLoadingPackId] = useState<number | null>(null);

  /** Add a pack without choice groups (all fixed items) */
  const addPack = useCallback(async (pack: ProductPack) => {
    setLoadingPackId(pack.id);
    try {
      await postItems(itemsToCartPayload(pack.items, source));
      onSuccess(pack, itemsToKeys(pack.items));
      trackPackAdded(pack);
      await refreshCart();
    } catch (e) {
      console.error('Error adding pack to cart:', e);
      throw e;
    } finally {
      setLoadingPackId(null);
    }
  }, [onSuccess, refreshCart, source]);

  /** Add a pack with explicit selected items (fixed + chosen options) */
  const addPackWithSelections = useCallback(async (pack: ProductPack, selectedItems: ProductPackItem[]) => {
    const allItems = [...pack.items, ...selectedItems];
    setLoadingPackId(pack.id);
    try {
      await postItems(itemsToCartPayload(allItems, source));
      onSuccess(pack, itemsToKeys(allItems));
      trackPackAdded(pack);
      await refreshCart();
    } catch (e) {
      console.error('Error adding pack to cart:', e);
      throw e;
    } finally {
      setLoadingPackId(null);
    }
  }, [onSuccess, refreshCart, source]);

  const removePack = useCallback(async (pack: ProductPack) => {
    setLoadingPackId(pack.id);
    try {
      await postItems(
        pack.items.map(item => ({ id: item.product_id, format_id: item.product_format_id, quantity: 0 }))
      );
      onRemoveSuccess(pack.id);
      await refreshCart();
    } catch (e) {
      console.error('Error removing pack from cart:', e);
      throw e;
    } finally {
      setLoadingPackId(null);
    }
  }, [onRemoveSuccess, refreshCart]);

  return { addPack, addPackWithSelections, removePack, loadingPackId };
}
